import { buildReport, money } from './report';
import { bandMessages, disclaimer, reportConfig, strongerSetup } from '../config/report';

const NAVY=[22,20,45], PURPLE=[48,32,79], GOLD=[181,139,34], BLUE=[64,141,186], INK=[36,42,58], GREY=[103,112,128];
const clean = text => String(text ?? '').replace(/[\u2013\u2014]/g,'-').replace(/[\u2018\u2019]/g,"'").replace(/[\u201c\u201d]/g,'"').replace(/\u00d7/g,'x').replace(/[^\x20-\x7E]/g,'');
const slug = text => String(text||'business').toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'').slice(0,50);

export async function createHealthReportPdf(answers, contact) {
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({unit:'mm',format:'a4',compress:true});
  const report=buildReport(answers);
  const W=210, M=17, CW=W-M*2;
  const write=(text,x,y,size=10,color=INK,weight='normal')=>{
    pdf.setFont('helvetica',weight);pdf.setFontSize(size);pdf.setTextColor(...color);
    pdf.text(clean(text),x,y);
  };
  const lines=(text,x,y,width,size=9,leading=4.4,color=INK,weight='normal')=>{
    pdf.setFont('helvetica',weight);pdf.setFontSize(size);pdf.setTextColor(...color);
    const arr=pdf.splitTextToSize(clean(text),width);
    arr.forEach((line,i)=>pdf.text(line,x,y+i*leading));
    return y+arr.length*leading;
  };
  const rect=(x,y,w,h,fill,r=0)=>{
    pdf.setFillColor(...fill);
    r?pdf.roundedRect(x,y,w,h,r,r,'F'):pdf.rect(x,y,w,h,'F');
  };
  const header=(title,subtitle,page)=>{
    rect(0,0,W,37,NAVY);rect(M,30,31,1.1,BLUE);
    write('BPA SOLUTIONS',M,14,13,[230,228,215],'bold');
    write('Smart Systems. Better Business.',M,21,8,[171,184,204]);
    write(title,M,47,17,NAVY,'bold');
    write(subtitle,M,54,8,GREY);
    pdf.setDrawColor(222,226,232);pdf.line(M,281,W-M,281);
    write('Timesheet-to-Invoice Health Check  |  Indicative assessment',M,286,7,GREY);
    write(page+' / 2',W-M-10,286,7,GREY);
  };
  const section=(title,y)=>{write(title,M,y,12,NAVY,'bold');rect(M,y+3,17,1,GOLD);return y+11;};
  header('Your Health Report',contact.businessName+'  |  Prepared for '+contact.firstName,1);
  rect(M,62,CW,35,[245,243,249],3);
  write(String(report.score),M+8,84,32,GOLD,'bold');
  write('/ 100',M+29,83,12,GREY);
  write(report.band.toUpperCase(),M+73,74,11,PURPLE,'bold');
  lines(bandMessages[report.band],M+73,81,CW-83,9,4.2);
  let y=101;
  y=section('Your four operational areas',y);
  report.areas.forEach(area=>{
    write(area.label,M,y,9,INK,'bold');write(area.score+' / 25',W-M-18,y,9,GOLD,'bold');
    rect(M,y+3,CW,3,[228,231,237],1);
    if(area.score)rect(M,y+3,CW*area.score/25,3,BLUE,1);
    y=lines(area.short,M,y+10,CW,7.5,3.5,GREY)+3;
  });
  y=section('Your top three risks',y);
  report.topRisks.forEach((area,i)=>{
    write((i+1)+'. '+area.label,M,y,9,PURPLE,'bold');
    y=lines(area.long,M,y+5,CW,7.5,3.6,INK)+2;
  });

  pdf.addPage();
  header('Your opportunity to improve','A practical roadmap based on your answers',2);
  let z=66;
  z=section('What your administration time costs',z);
  rect(M,z-3,CW,28,[245,243,249],3);
  write(money(report.annualCost),M+8,z+9,22,GOLD,'bold');
  write('per year',M+77,z+8,10,INK);
  write(answers.q8+' hours/week x '+money(answers.q9)+'/hour x 52 weeks',M+8,z+17,8,GREY);
  z+=39;
  write('Illustrative value of recovering 50% of this time: '+money(report.annualCost*.5),M,z,10,PURPLE,'bold');
  z=lines('Illustrative estimate based on your own answers. Not a guarantee of savings.',M,z+7,CW,8,4,GREY)+13;
  z=section('What a stronger setup looks like',z);
  strongerSetup.forEach(item=>{rect(M,z-3,2,2,BLUE,1);z=lines(item,M+6,z,CW-7,9,4.7)+4;});
  z=section('Your recommended next step',z+2);
  const next=report.grade==='A'
    ? ['Want to see what this could look like for your business?','Book a free 20-minute process review. I will walk through your results with you and show you a real system built for a business like yours.',reportConfig.caseStudy,reportConfig.bookingUrl]
    : report.grade==='B'
      ? ['Your next step','Download your report and review the risks above. If you would like to talk it through, reply to your report email or contact us.',reportConfig.contactEmail]
      : ['Keep this for later','Your report is available to keep. If your situation changes, we are happy to help.'];
  z=lines(next[0],M,z,CW,10,5,PURPLE,'bold')+3;
  z=lines(next[1],M,z,CW,8.5,4.3)+4;
  if(next[2])z=lines(next[2],M,z,CW,8,4,GREY)+3;
  if(next[3])z=lines(next[3],M,z,CW,8,4,BLUE)+2;
  lines(disclaimer,M,271,CW,7,3,GREY);
  return pdf;
}

export async function createHealthReportAttachment(answers,contact){
  const pdf=await createHealthReportPdf(answers,contact);
  return {filename:'BPA-Timesheet-Health-Report.pdf',content:pdf.output('datauristring').split(',')[1],content_type:'application/pdf'};
}

export async function downloadHealthReport(answers, contact) {
  const pdf=await createHealthReportPdf(answers,contact);
  pdf.save('BPA-Health-Report-'+slug(contact.businessName)+'.pdf');
}